import { useSyncExternalStore } from 'react';

// Installing the dashboard as an app. Chrome, Edge and Android Chrome fire one install
// prompt early, which the account menu and the last onboarding screen open later; Safari,
// iPhone and iPad users add the app themselves, so they get the steps instead.

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

// The installed app and the browser share storage on Chrome and Edge, so a tab can tell the
// app is already on this device once it has been installed or opened.
const INSTALLED_KEY = 'cs_app_installed';

// Chrome's own install bar reads like the staff phone app, so it is held back until this browser
// has seen a finished setup, and always on sign-in and setup pages. The menu and the last setup
// screen can still open the prompt.
const SETUP_DONE_KEY = 'cs_setup_done';
const HELD_PATHS =
  /^\/(login|signup|forgot-password|reset-password|oauth|dashboard\/oauth|welcome|onboarding)(\/|$)/;

let deferred: InstallPromptEvent | null = null;
let installed = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function rememberInstalled(value: boolean) {
  installed = value;
  try {
    if (value) localStorage.setItem(INSTALLED_KEY, '1');
    else localStorage.removeItem(INSTALLED_KEY);
  } catch {
    // Private windows can refuse storage; this visit still knows.
  }
  notify();
}

/** Called by the dashboard shell, so the next visit knows whether setup is finished. */
export function rememberSetupDone(done: boolean) {
  try {
    if (done) localStorage.setItem(SETUP_DONE_KEY, '1');
    else localStorage.removeItem(SETUP_DONE_KEY);
  } catch {
    // Without storage the bar just stays held back.
  }
}

/** Whether Android Chrome may show its own install bar on this page. */
export function installBarAllowed(path: string): boolean {
  if (HELD_PATHS.test(path)) return false;
  try {
    return localStorage.getItem(SETUP_DONE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Call before the first render: the browser's prompt arrives once, soon after load. It is
 * kept either way; preventDefault only stops Chrome's own bar where it isn't wanted yet.
 */
export function captureInstallPrompt() {
  try {
    installed = localStorage.getItem(INSTALLED_KEY) === '1';
  } catch {
    installed = false;
  }
  if (runningAsApp()) rememberInstalled(true);
  // Browsers only offer the prompt while the app is not installed, so an offer also means an
  // earlier install was removed.
  window.addEventListener('beforeinstallprompt', (event) => {
    if (!installBarAllowed(window.location.pathname)) event.preventDefault();
    deferred = event as InstallPromptEvent;
    rememberInstalled(false);
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    rememberInstalled(true);
  });
  // Chrome and Edge can also answer directly, through related_applications in the manifest.
  const related = (navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> })
    .getInstalledRelatedApps;
  related
    ?.call(navigator)
    .then((apps) => apps.length > 0 && rememberInstalled(true))
    .catch(() => {});
}

/** Whether the app is known to be installed on this device. */
export function appInstalled() {
  return installed;
}

export function runningAsApp() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

/** Whether the browser's own install prompt is ready, and a way to open it once. */
export function useInstallPrompt() {
  const ready = useSyncExternalStore(subscribe, () => deferred !== null, () => false);
  const isInstalled = useSyncExternalStore(subscribe, appInstalled, () => false);
  return {
    ready,
    installed: isInstalled,
    async open() {
      const event = deferred;
      if (!event) return false;
      deferred = null;
      notify();
      await event.prompt();
      return (await event.userChoice).outcome === 'accepted';
    },
  };
}

export type InstallGuide = 'ios' | 'android' | 'mac-safari' | 'firefox' | 'desktop';

/** Which install steps fit this browser. iPadOS reports a Mac, so touch points tell them apart. */
export function installGuide(userAgent: string, touchPoints = 0): InstallGuide {
  if (/iPhone|iPad|iPod/.test(userAgent) || (/Macintosh/.test(userAgent) && touchPoints > 1)) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  if (/Firefox\//.test(userAgent)) return 'firefox';
  if (
    /Macintosh/.test(userAgent) &&
    /Safari\//.test(userAgent) &&
    !/Chrome\/|Chromium\/|Edg\//.test(userAgent)
  )
    return 'mac-safari';
  return 'desktop';
}

export const INSTALL_STEPS: Record<InstallGuide, string[]> = {
  ios: ['Tap the Share button in the browser.', 'Tap Add to Home Screen, then Add.'],
  android: ['Open the browser menu (⋮).', 'Tap Install app or Add to Home screen.'],
  'mac-safari': ['In the menu bar, choose File, then Add to Dock.', 'Click Add.'],
  firefox: [
    "Firefox can't install websites as apps. Open dash.callsyncapp.com in Chrome, Edge or Safari and install it from there.",
  ],
  desktop: [
    'Click the install icon at the right end of the address bar, or open the browser menu and choose Install CallSync.',
  ],
};
